import type { JapanHotel } from "@/types/generated";
import type { HotelMetaDataItem } from "@/components/organisms/map-view/japan-map/japanCountryMap";
import hotelAdjustedCoordinates from "@/../public/data/hotelAdjustedCoordinates.json";
import tokyoWardMap from "@/../public/data/toykyoWardMap.json";

/**
 * Type for the adjusted coordinates fallback data
 */
type AdjustedCoordinatesMap = Record<string, {
  adjustedLatitude: number;
  adjustedLongitude: number;
}>;

/**
 * Minimum hotel fields required for map transformation
 * Supports both item_name (from japanAreas) and name (from HotelListQuery)
 */
export type MinimalHotelForMap = {
  slug?: string | null;
  item_name?: string | null;
  name?: string | null;
  properties?: {
    latitude?: string | null;
    longitude?: string | null;
    adjust_latitude?: string | number | null;
    adjust_longtitude?: string | number | null;
    [key: string]: unknown;
  } | null;
};

/**
 * Transforms hotel data from CMS API to the format expected by map components
 * Falls back to hotelAdjustedCoordinates.json if coordinates are not in API
 * 
 * @param hotels - Array of hotels from CMS API (can be partial JapanHotel objects)
 * @returns Array of hotel metadata formatted for map display
 */
export const transformHotelsForMap = (hotels: MinimalHotelForMap[]): HotelMetaDataItem[] => {
  const coordinatesMap = hotelAdjustedCoordinates as AdjustedCoordinatesMap;
  return hotels.map((hotel) => {
    const slug = hotel.slug || '';
    const fallbackCoords = slug ? coordinatesMap[slug] : undefined;
    
    // Get coordinates with proper null/undefined checking
    const apiAdjustedLongitude = hotel.properties?.adjust_longtitude ?? null;
    const apiAdjustedLatitude = hotel.properties?.adjust_latitude ?? null;
    
    const longitude = apiAdjustedLongitude ?? fallbackCoords?.adjustedLongitude ?? hotel.properties?.longitude;
    const latitude = apiAdjustedLatitude ?? fallbackCoords?.adjustedLatitude ?? hotel.properties?.latitude;
    
    // Convert to number, handling null by converting to undefined
    let parsedLongitude: number | undefined = undefined;
    if (longitude !== null && longitude !== undefined) {
      parsedLongitude = typeof longitude === 'string' ? Number.parseFloat(longitude) : longitude;
    }
    
    let parsedLatitude: number | undefined = undefined;
    if (latitude !== null && latitude !== undefined) {
      parsedLatitude = typeof latitude === 'string' ? Number.parseFloat(latitude) : latitude;
    }
    
    return {
      // Prefer item_name, fall back to name (for HotelListQuery compatibility)
      name: hotel.item_name || hotel.name || undefined,
      slug: slug,
      // The map components only use a subset of properties, but we need to pass the full object
      // to satisfy the type. The actual properties object contains all the fields.
      properties: hotel.properties as HotelMetaDataItem['properties'],
      // Priority: 1) API adjusted coords, 2) JSON fallback, 3) If neither exists, use original coords
      adjustedLongitude: parsedLongitude,
      adjustedLatitude: parsedLatitude,
    };
  });
};

/**
 * Type for the transformed hotel data matching toykyoWardMap.json and osakaWardMap.json structure
 * Extended to be compatible with Hotel type used in map rendering
 * Used for both Tokyo 23 Wards and Osaka 24 Wards
 */
export interface TokyoWardMapHotel {
  name: string;
  slug: string;
  hotel_images: {
    desktop: {
      media: {
        cloudflare_url?: string;
      };
    }[];
  };
  properties: {
    prefecture: string;
    hotel_name: string;
    brand: {
      order: number;
      documentId: string;
    }[];
    latitude: string;
    longitude: string;
  };
  adjustedLongitude: number;
  adjustedLatitude: number;
  // Optional fields to make it compatible with usage in japanPrefectureMap
  documentId?: string;
  redirect?: unknown;
}

// Alias for Osaka - same structure as Tokyo
export type OsakaWardMapHotel = TokyoWardMapHotel;

/**
 * Simplified type for Osaka ward hotels matching osakaWardMap.json structure
 * Only contains name and slug for simpler filtering
 */
export interface OsakaWardHotelSimple {
  name: string;
  slug: string;
}

/**
 * Type for GraphQL JapanHotel input (from hotel.japanHotels)
 */
export interface GraphQLJapanHotelInput {
  name: string;
  slug: string | null;
  properties?: {
    prefecture?: string | null;
    hotel_name?: string | null;
    brand?: {
      order: number;
      documentId: string;
    }[];
    latitude?: string | null;
    longitude?: string | null;
    ajust_longitude?: number | null;
    ajust_latitude?: number | null;
  } | null;
  hotel_images?: {
    slider_desktop?: ({
      image_url?: {
        cloudflare_url?: string | null;
        [key: string]: unknown;
      } | null;
      [key: string]: unknown;
    } | null)[];
  } | null;
  adjustedLongitude?: number;
  adjustedLatitude?: number;
}

/**
 * Transforms GraphQL JapanHotel data to match toykyoWardMap.json structure
 * This converts the nested GraphQL structure to the flattened structure used by the map
 * 
 * @param data - Array of hotels from GraphQL (hotel.japanHotels)
 * @returns Array of hotels matching toykyoWardMap.json structure
 */
export function transformJapanHotels(data: (GraphQLJapanHotelInput | null)[]): TokyoWardMapHotel[] {
  const coordinatesMap = tokyoWardMap as AdjustedCoordinatesMap;
  
  return data
    .filter((hotel): hotel is GraphQLJapanHotelInput => hotel !== null)
    .map((hotel: GraphQLJapanHotelInput): TokyoWardMapHotel => {
      const {
        name,
        slug,
        properties,
        hotel_images
      } = hotel;
      
      const fallbackCoords = slug ? coordinatesMap[slug] : undefined;
        
      // Transform images → desktop[]
      const desktopImages =
        hotel_images?.slider_desktop?.map((img) => ({
          media: {
            cloudflare_url: img?.image_url?.cloudflare_url || undefined,
          },
        })) || [];

      return {
        name,
        slug: slug || '',
        hotel_images: {
          desktop: desktopImages,
        },
        properties: {
          prefecture: properties?.prefecture || '',
          hotel_name: properties?.hotel_name || '',
          brand: properties?.brand || [],
          latitude: properties?.latitude || '',
          longitude: properties?.longitude || '',
        },
        adjustedLongitude: 
          properties?.ajust_longitude || 
          fallbackCoords?.adjustedLongitude || 0,
        adjustedLatitude: 
          properties?.ajust_latitude || 
          fallbackCoords?.adjustedLatitude || 0,
      };
    });
}

/**
 * Transforms GraphQL JapanHotel data to match osakaWardMap.json structure
 * This creates a simple structure with only name and slug fields
 * 
 * @param data - Array of hotels from GraphQL (hotel.japanHotels)
 * @returns Array of Osaka hotels with simple structure (name and slug only)
 */
export function transformOsakaHotels(data: (GraphQLJapanHotelInput | null)[]): OsakaWardHotelSimple[] {
  return data
    .filter((hotel): hotel is GraphQLJapanHotelInput => hotel !== null)
    .map((hotel: GraphQLJapanHotelInput): OsakaWardHotelSimple => ({
      name: hotel.name,
      slug: hotel.slug || '',
    }));
}
